// OP-5494 — session-scoped Pinia cache for the social-network catalog.
//
// The catalog is a small, mostly-static lookup (≤ 20 entries seeded by OPHUB
// system data) shared by every SocialProfileFormModal mount across the app —
// organisation tab today, contact tab in OP-5495. A Pinia store gives us a
// single fetch per session with deduped concurrent calls, mirroring the
// pattern used by `useLegalFormCatalog` / `useActivityCodeCatalog` but
// promoted to a store because the data is consumed across modules.
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { logger } from "@/infrastructure/services/LoggerService";
import { fetchSocialNetworks } from "@/shared/infrastructure/api/socialNetworkApi";
export const useSocialNetworkStore = defineStore("socialNetwork", () => {
    const networks = ref([]);
    const loading = ref(false);
    const errorKey = ref(null);
    let inFlight = null;
    /**
     * Triggers the (cached) catalog fetch. Safe to call from multiple components
     * concurrently — the second caller awaits the first request's promise. Stays
     * cached for the lifetime of the Pinia instance (one session). On failure
     * sets `errorKey` to a translation key the consumer can surface as a banner.
     */
    async function load() {
        if (networks.value.length > 0)
            return;
        if (inFlight) {
            await inFlight;
            return;
        }
        loading.value = true;
        errorKey.value = null;
        inFlight = fetchSocialNetworks();
        try {
            const result = await inFlight;
            // BFF preserves OPHUB's DisplayOrder ascending, but defend against an
            // out-of-order response: sort by displayOrder then code so the dropdown
            // stays deterministic.
            networks.value = [...result].sort((a, b) => {
                if (a.displayOrder !== b.displayOrder)
                    return a.displayOrder - b.displayOrder;
                return a.code.localeCompare(b.code);
            });
        }
        catch (err) {
            logger.error("[useSocialNetworkStore] load failed:", err);
            errorKey.value = "socialProfile.common.catalogLoadError";
        }
        finally {
            inFlight = null;
            loading.value = false;
        }
    }
    /** Resolves a catalog entry by id — convenience for FormModal pre-fill. */
    const byId = computed(() => (id) => networks.value.find((n) => n.id === id));
    return {
        networks,
        loading,
        errorKey,
        load,
        byId,
    };
});
